/* ajaxed-atc.js — Upcart / Kaching compatibility + theme cart scroll unlock
 *
 * Upcart owns the cart UI. Shrine <cart-drawer>.open() still adds
 * body.overflow-hidden. Upcart scroll-lock only runs when
 * overrideScrollLocking is on (it is not here), so after Upcart closes the
 * theme lock stays and the page freezes. Prevent / clear that lock whenever
 * Upcart is present.
 */
(() => {
  const hasUpcart = () =>
    Boolean(
      window.upcartOpenCart ||
        window.upcartCloseCart ||
        window.upcartSettings ||
        document.getElementById('upCart') ||
        document.getElementById('UpcartPopupBackDrop') ||
        document.getElementById('UpcartPopup') ||
        document.querySelector('[data-upcart-cart-button]') ||
        document.querySelector('script[src*="upcart"]')
    );

  const isKachingManagedForm = (form) =>
    Boolean(
      form?.querySelector('[name="properties[__kaching_bundles]"]') ||
        form?.querySelector('input[name*="__kaching_bundles"]') ||
        form?.closest('kaching-bundles-block') ||
        document.querySelector('kaching-bundles-block')
    );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form) return;
      if (hasUpcart() || isKachingManagedForm(form)) return;
    },
    true
  );

  function unlockThemeCart() {
    document.body.classList.remove('overflow-hidden', 'ccd-locked');
    document.documentElement.classList.remove('overflow-hidden');
    if (document.body.style.overflow === 'hidden') {
      document.body.style.removeProperty('overflow');
    }
    if (document.documentElement.style.overflow === 'hidden') {
      document.documentElement.style.removeProperty('overflow');
    }

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    if (typeof drawer.close === 'function') {
      try {
        drawer.close();
      } catch (_) {
        /* ignore */
      }
    }
    drawer.classList.remove('active');
  }

  function isUpcartVisible() {
    const el = document.getElementById('upCart');
    if (!el) return false;
    if ([...el.classList].some((c) => c.startsWith('styles_active'))) return true;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    // Upcart often stays in DOM; treat non-zero size + high z as open when active class missing
    return [...el.classList].some((c) => c.includes('active'));
  }

  let patched = false;

  function patchCartDrawer() {
    if (patched || !hasUpcart()) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    patched = true;

    const proto = Object.getPrototypeOf(drawer);
    if (!proto || proto.__ffUpcOpenPatched) {
      // Still wrap this instance if proto already patched elsewhere
    } else {
      proto.__ffUpcOpenPatched = true;
      const origOpen = proto.open;
      proto.open = function (triggeredBy) {
        if (!hasUpcart()) {
          return origOpen.call(this, triggeredBy);
        }
        // Cart icon / ATC: prefer Upcart; never leave theme scroll lock behind.
        try {
          if (typeof window.upcartOpenCart === 'function') {
            window.upcartOpenCart();
          }
        } catch (_) {
          /* ignore */
        }
        // If something already locked body (race), clear theme lock.
        requestAnimationFrame(unlockThemeCart);
        setTimeout(unlockThemeCart, 0);
        setTimeout(unlockThemeCart, 120);
        setTimeout(unlockThemeCart, 400);
        return undefined;
      };
    }

    // Belt-and-suspenders on this node in case open is own-property
    if (!drawer.__ffUpcInstancePatched) {
      drawer.__ffUpcInstancePatched = true;
      const instOpen = drawer.open.bind(drawer);
      drawer.open = function (triggeredBy) {
        if (!hasUpcart()) return instOpen(triggeredBy);
        try {
          if (typeof window.upcartOpenCart === 'function') {
            window.upcartOpenCart();
          }
        } catch (_) {
          /* ignore */
        }
        requestAnimationFrame(unlockThemeCart);
        setTimeout(unlockThemeCart, 0);
        setTimeout(unlockThemeCart, 120);
        setTimeout(unlockThemeCart, 400);
      };
    }
  }

  function subscribeUpcart() {
    if (!hasUpcart()) return;

    const onOpened = () => {
      unlockThemeCart();
      [0, 50, 150, 400].forEach((ms) => setTimeout(unlockThemeCart, ms));
    };
    const onClosed = () => {
      unlockThemeCart();
      [0, 50, 150, 400].forEach((ms) => setTimeout(unlockThemeCart, ms));
    };

    try {
      if (typeof window.upcartSubscribeCartOpened === 'function') {
        window.upcartSubscribeCartOpened(onOpened);
      }
      if (typeof window.upcartSubscribeCartClosed === 'function') {
        window.upcartSubscribeCartClosed(onClosed);
      }
    } catch (_) {
      /* ignore */
    }

    const EV_OPEN = 'aftersell-upcart:public-events:cart-opened';
    const EV_CLOSE = 'aftersell-upcart:public-events:cart-closed';
    window.addEventListener(EV_OPEN, onOpened);
    window.addEventListener(EV_CLOSE, onClosed);
    document.addEventListener('cart-closed', onClosed);
    document.addEventListener('cart-opened', onOpened);
  }

  // If theme left overflow-hidden and Upcart is not visible, clear it.
  function reconcileStuckLock() {
    if (!hasUpcart()) return;
    if (!document.body.classList.contains('overflow-hidden')) return;
    const drawerActive = Boolean(document.querySelector('cart-drawer.active'));
    if (drawerActive) {
      // Theme drawer genuinely open — leave it (no Upcart path).
      // But if Upcart is also the store cart, prefer clearing.
      if (document.getElementById('upCart') || window.upcartOpenCart) {
        unlockThemeCart();
      }
      return;
    }
    // Stuck lock with no active theme drawer
    unlockThemeCart();
  }

  const bodyObs = new MutationObserver(() => {
    if (!hasUpcart()) return;
    if (document.body.classList.contains('overflow-hidden')) {
      // Theme lock appeared — if Upcart owns cart, strip it shortly
      setTimeout(reconcileStuckLock, 0);
      setTimeout(reconcileStuckLock, 200);
      setTimeout(reconcileStuckLock, 600);
    }
  });

  function boot() {
    if (!document.body) return;
    bodyObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    patchCartDrawer();
    subscribeUpcart();
    customElements.whenDefined('cart-drawer').then(() => {
      patched = false; // allow patch once element is upgraded
      patchCartDrawer();
    }).catch(() => {});
    [0, 500, 1500, 3000].forEach((ms) => {
      setTimeout(() => {
        patchCartDrawer();
        subscribeUpcart();
        reconcileStuckLock();
      }, ms);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
